import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import DiscoverArtworkGrid from '../../components/artozphere/DiscoverArtworkGrid';
import ArtozphereCartBar from '../../components/artozphere/ArtozphereCartBar';

const categories = ['Painting', 'Photography', 'Digital', 'Sculpture', 'All'];
const collections = ['Featured', 'New Arrivals', 'Most Popular', 'All'];
const priceRanges = [
  { label: 'All Prices', value: 'all' },
  { label: 'Under $1,000', value: '0-1000' },
  { label: '$1,000 - $5,000', value: '1000-5000' },
  { label: '$5,000 - $20,000', value: '5000-20000' },
  { label: 'Over $20,000', value: '20000-above' },
];

const ArtozpherePage = () => {
  const [artworks, setArtworks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [liveViews, setLiveViews] = useState({});
  const [activeFilters, setActiveFilters] = useState({
    category: 'All',
    collection: 'All',
    priceRange: 'all',
  });
  const [showDropdown, setShowDropdown] = useState({
    category: false,
    collection: false,
    priceRange: false,
  });

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setLoadError(null);

    axiosInstance
      .get('/artworks/marketplace')
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.artworks || [];
        setArtworks(data);
      })
      .catch((err) => {
        console.error('Failed to fetch artworks:', err);
        if (!cancelled) setLoadError('Failed to load artworks. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (artworks.length === 0) return;

    const viewsMap = {};
    artworks.forEach((art) => {
      viewsMap[art._id] = art.views || 0;
    });
    setLiveViews(viewsMap);
  }, [artworks]);

  const getPriceValue = (priceString) => {
    if (!priceString) return 0;
    const numericValue = parseInt(String(priceString).replace(/[^0-9]/g, ''), 10);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  };

  const filterArtworks = () =>
    artworks.filter((artwork) => {
      const price = getPriceValue(artwork.price);
      const matchesCategory =
        activeFilters.category === 'All' || artwork.category === activeFilters.category;
      const matchesCollection =
        activeFilters.collection === 'All' || artwork.collection === activeFilters.collection;

      let matchesPrice = true;
      if (activeFilters.priceRange !== 'all') {
        const [min, max] = activeFilters.priceRange.split('-');
        const minPrice = parseInt(min, 10) || 0;

        if (max === 'above') {
          matchesPrice = price >= minPrice;
        } else {
          const maxPrice = parseInt(max, 10) || Infinity;
          matchesPrice = price >= minPrice && price <= maxPrice;
        }
      }

      return matchesCategory && matchesCollection && matchesPrice;
    });

  const handleDropdownClick = (dropdownName) => {
    setShowDropdown((prev) => ({
      category: false,
      collection: false,
      priceRange: false,
      [dropdownName]: !prev[dropdownName],
    }));
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Navbar />
      <div className="ml-0 md:ml-20 pb-24 md:pb-8">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16" id="BrowseCollections">
          <div className="mb-8">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4 px-3 sm:px-4 md:px-5">
              <div>
                <h1 className="text-3xl sm:text-5xl md:text-6xl font-serif mb-2">Artozphere</h1>
                <h2 className="text-2xl sm:text-3xl font-serif mb-2 text-gray-800">Discover</h2>
                <p className="text-gray-600 text-sm sm:text-lg">
                  These Creators have produced works that are highly valued and in demand.
                </p>
              </div>
              <ArtozphereCartBar />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 my-2 relative pl-3 sm:pl-4 md:pl-5">
              <div className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => handleDropdownClick('category')}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-full border border-gray-300 hover:border-gray-400 flex items-center gap-2 text-sm sm:text-base"
                >
                  <span className="text-lg">⊞</span> Category
                </button>
                {showDropdown.category && (
                  <div className="absolute top-12 left-0 bg-white shadow-lg rounded-lg p-2 z-50 min-w-[150px]">
                    {categories.map((category) => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => {
                          setActiveFilters((prev) => ({ ...prev, category }));
                          setShowDropdown((prev) => ({ ...prev, category: false }));
                        }}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm sm:text-base"
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => handleDropdownClick('collection')}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-full border border-gray-300 hover:border-gray-400 flex items-center gap-2 text-sm sm:text-base"
                >
                  <span className="text-lg">□</span> Collection
                </button>
                {showDropdown.collection && (
                  <div className="absolute top-12 left-0 bg-white shadow-lg rounded-lg p-2 z-50 min-w-[150px]">
                    {collections.map((collection) => (
                      <button
                        key={collection}
                        type="button"
                        onClick={() => {
                          setActiveFilters((prev) => ({ ...prev, collection }));
                          setShowDropdown((prev) => ({ ...prev, collection: false }));
                        }}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm sm:text-base"
                      >
                        {collection}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => handleDropdownClick('priceRange')}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-full border border-gray-300 hover:border-gray-400 flex items-center gap-2 text-sm sm:text-base"
                >
                  <span className="text-lg">$</span> Price range
                </button>
                {showDropdown.priceRange && (
                  <div className="absolute top-12 left-0 bg-white shadow-lg rounded-lg p-2 z-50 min-w-[150px]">
                    {priceRanges.map((range) => (
                      <button
                        key={range.value}
                        type="button"
                        onClick={() => {
                          setActiveFilters((prev) => ({ ...prev, priceRange: range.value }));
                          setShowDropdown((prev) => ({ ...prev, priceRange: false }));
                        }}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer rounded text-sm sm:text-base"
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {Object.values(activeFilters).some((f) => f !== 'All' && f !== 'all') && (
              <div className="flex flex-wrap gap-2 mt-4">
                {Object.entries(activeFilters).map(
                  ([key, value]) =>
                    value !== 'All' &&
                    value !== 'all' && (
                      <span
                        key={key}
                        className="px-3 py-1 bg-gray-100 rounded-full text-sm sm:text-base flex items-center gap-2"
                      >
                        {value}
                        <button
                          type="button"
                          onClick={() =>
                            setActiveFilters((prev) => ({
                              ...prev,
                              [key]: key === 'priceRange' ? 'all' : 'All',
                            }))
                          }
                          className="text-gray-500 hover:text-gray-700"
                        >
                          ×
                        </button>
                      </span>
                    )
                )}
              </div>
            )}
          </div>

          <DiscoverArtworkGrid
            artworks={filterArtworks()}
            liveViews={liveViews}
            isLoading={isLoading}
            emptyMessage={loadError || 'No artworks match your filters.'}
          />
        </div>
      </div>
    </div>
  );
};

export default ArtozpherePage;
